import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CampaignChannel } from "../../Engagements/EngagementInterfaces";
import { setCampaignStatus } from "src/services/ChatbotAPI";
import { useCampaignDraft } from "src/layouts/Engagements/Campaigns/shared/UseCampaignDraft";
import { useChatCampaignSetup, type ChatCampaignSetupResult } from "./UseChatCampaignSetup";
import { useChatEmailCampaignWizard, type ChatWizardStepSnapshot } from "./UseChatEmailCampaignWizard";
import { useChatWhatsAppCampaignWizard, type ChatWhatsAppStepSnapshot } from "./useChatWhatsAppCampaignWizard";
import { useChatTemplateCampaignWizard, type ChatTemplateStepSnapshot } from "./UseChatTemplateCampaignWizard";

export type ChatCampaignFlowPhase = "setup" | "email" | "template" | "whatsapp";

export type ChatCampaignFlowSnapshot =
  | { phase: "setup"; result?: ChatCampaignSetupResult }
  | { phase: "email"; snapshot: ChatWizardStepSnapshot }
  | { phase: "template"; snapshot: ChatTemplateStepSnapshot }
  | { phase: "whatsapp"; snapshot: ChatWhatsAppStepSnapshot };

interface UseChatCampaignFlowParams {
  onAdvance: (_snapshot: ChatCampaignFlowSnapshot) => void;
  presetChannel?: CampaignChannel;
}

export const useChatCampaignFlow = ({ onAdvance, presetChannel }: UseChatCampaignFlowParams) => {
  const [phase, setPhase] = useState<ChatCampaignFlowPhase>("setup");
  const [campaignData, setCampaignData] = useState<Partial<ChatCampaignSetupResult>>({});
  const draftController = useCampaignDraft();

  const setup = useChatCampaignSetup({
    draftController,
    presetChannel,
    onSubmit: result => {
      onAdvance({ phase: "setup", result });
      setCampaignData(result);
      if (result.channel === "whatsapp") {
        setPhase("whatsapp");
      } else {
        setPhase(result.isAIGeneratorOn ? "email" : "template");
      }
    },
  });

  const emailWizard = useChatEmailCampaignWizard({
    campaignData,
    onAdvance: snapshot => onAdvance({ phase: "email", snapshot }),
    draftController,
  });

  const backToSetup = () => setPhase("setup");

  const templateWizard = useChatTemplateCampaignWizard({
    campaignData,
    onAdvance: snapshot => onAdvance({ phase: "template", snapshot }),
    onBackToSetup: backToSetup,
    draftController,
  });

  const whatsAppWizard = useChatWhatsAppCampaignWizard({
    campaignData,
    onAdvance: snapshot => onAdvance({ phase: "whatsapp", snapshot }),
    onBackToSetup: backToSetup,
  });

  // Re-seed the destination wizard the moment setup hands off fresh
  // campaignData, so campaignName/sendTo/etc. aren't stuck at the stale
  // empty values captured when these hooks first mounted.
  const campaignDataRef = useRef(campaignData);
  useEffect(() => {
    if (campaignDataRef.current === campaignData) {
      return;
    }
    campaignDataRef.current = campaignData;
    if (!campaignData.campaignName) {
      return;
    }
    if (phase === "email") {
      emailWizard.reset();
    } else if (phase === "template") {
      templateWizard.reset();
    }
  }, [campaignData, emailWizard, phase, templateWizard]);

  const { closeDraft } = draftController;
  const resetFlow = useCallback(() => {
    closeDraft();
    setCampaignStatus("none");
    setPhase("setup");
    setCampaignData({});
    setup.reset();
    emailWizard.reset();
    templateWizard.reset();
    whatsAppWizard.wizard.reset();
  }, [closeDraft, emailWizard, setup, templateWizard, whatsAppWizard]);

  return useMemo(
    () => ({ phase, setup, campaignData, emailWizard, templateWizard, whatsAppWizard, backToSetup, resetFlow }),
    [phase, setup, campaignData, emailWizard, templateWizard, whatsAppWizard, resetFlow]
  );
};
